import React, { useDebugValue, useEffect } from 'react';
import { shallowEqual } from 'react-redux';
import { useGetNotificationsSettingsQuery } from 'src/services/api';
import { FlagsContext, PortalConfigContext } from '../context';
import { useGetInboxNotificationsQuery } from '../services/api/inboxApi';
import { formatNotificationCount } from '../utils/NotificationsUtils';
import { useNotificationsCounts } from './useNotificationsCount';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import {
  setChannelIdToCompanyId,
  setTotalUnreadMessagesByCompany,
} from 'src/store/messages/actions';
import { useChatClient } from 'src/legacy/components/Chat/useChatClient';
import { setTotalUnreadMessages } from 'src/store/messages/actions';
import { DefaultStreamChatGenerics } from 'stream-chat-react';
import { Channel, Event, ExtendableGenerics, StreamChat } from 'stream-chat';
import { getChannelsWithUser } from 'src/utils/MessagingUtils';

const channelWithCompanyID = (
  channel: Channel<DefaultStreamChatGenerics>,
): channel is Channel<DefaultStreamChatGenerics> & {
  data: { companyID: string };
} => {
  return Boolean(
    channel.data &&
      'companyID' in channel.data &&
      channel.data?.companyID &&
      typeof channel.data.companyID === 'string',
  );
};

/**
 * A custom React hook that calculates the notification count for internal or client users
 * and updates the page title with the count and title.
 *
 * This hook uses data from the Redux store to determine notification counts and
 * uses the `useEffect` hook to update the document title based on the notification counts.
 *
 * @returns {void}
 */
export const useUserNotification = (isClientUser: boolean) => {
  // Fetch inbox notifications using the useGetInboxNotificationsQuery hook
  const { data: inboxNotifications = [] } = useGetInboxNotificationsQuery();
  const portalConfig = React.useContext(PortalConfigContext);

  const { data: notificationSettings } = useGetNotificationsSettingsQuery(
    undefined,
    {
      skip: isClientUser,
    },
  );
  const dispatch = useAppDispatch();
  const chatClient = useChatClient();
  const { EnableManyCompanies } = React.useContext(FlagsContext);
  const currentUserChatId = useAppSelector(
    (state) => state.user.data?.getstreamId,
  );
  const channelIdToCompanyId = useAppSelector(
    (state) => state.messages.channelIdToCompanyId,
  );

  const disabledProductNotification = notificationSettings?.disableInProduct;
  const disabledMessagesNotifications =
    notificationSettings?.notifyAbout.newMessages.disableInProduct;
  // Extract relevant state properties using useSelector from the Redux store
  const {
    activeCompanyId,
    isClient,
    totalUnreadMessages,
    clients,
    internalUsers,
    companyMap,
  } = useAppSelector(
    (state) => ({
      formCompletedNotifications:
        state.notifications.formCompletedNotifications || [],
      formRequestNotifications:
        state.notifications.formRequestNotifications || [],
      totalUnreadMessages: state.messages.totalUnreadMessages || 0,
      newInvoiceNotifications:
        state.notifications.newInvoiceNotifications || [],
      isClient: state.user.isClient,
      fileNotifications: state.notifications.fileNotifications,
      esigRequestNotifications: state.notifications.esigRequestNotifications,
      esigRequestCompleteNotifications:
        state.notifications.esigRequestCompleteNotifications,
      clients: state.clients.clients,
      internalUsers: state.users.internalUsers,
      companyMap: state.clients.companyDataMap,
      activeCompanyId: state.activeCompany.activeCompanyId,
    }),
    shallowEqual,
  );

  const activeCompanyNotifications = inboxNotifications.filter(
    (notification) => {
      if (notification.isRead) return false;
      // TODO(@foley: Remove this once multiple companies is live. All notifications
      //              will have company IDs at that point.
      if (!notification.companyId) return true;
      return notification.companyId === activeCompanyId;
    },
  );

  // Calculate the count of unread inbox notifications
  const notificationCounts = useNotificationsCounts(
    activeCompanyNotifications,
    [...clients, ...internalUsers],
    companyMap,
  );

  /**
   * Calculates the notification count for internal users by summing up
   * the total unread messages, unread inbox notifications, and form notifications.
   *
   * @returns {number} The total notification count for internal users.
   */
  const getInternalUserNotificationCount = (): number => {
    const disableMessagesNotifications =
      notificationSettings?.notifyAbout.newMessages.disableInProduct;

    // only count unread messages when the in-product notifications are allowed
    const unreadMessages = disableMessagesNotifications
      ? 0
      : totalUnreadMessages;

    return unreadMessages + notificationCounts.unreadNotificationCount;
  };

  /**
   * Calculates the notification count for client users by summing up
   * the total unread messages, unread inbox notifications, billing notifications, file notifications and form notifications.
   *
   * @returns {number} The total notification count for client users.
   */
  const getClientUserNotificationCount = (): number => {
    return totalUnreadMessages + notificationCounts.unreadNotificationCount;
  };

  // Calculate the total notification count for client and internal users
  const totalClientNotifications = getClientUserNotificationCount();
  const totalInternalNotifications = getInternalUserNotificationCount();

  useDebugValue({
    totalClientNotifications,
    totalInternalNotifications,
    totalUnreadMessages,
  });

  /**
   * We need to fetch the unread count for each company when the app mounts.
   * This function fetches all current user's channels and sets the unread count for each company.
   */
  const fetchUnreadMessagesCountByCompany = async (
    client: StreamChat<DefaultStreamChatGenerics>,
  ) => {
    if (!currentUserChatId) return;
    const userChannels = await getChannelsWithUser(
      { memberId: currentUserChatId, companyId: null },
      client,
    );

    userChannels.forEach((channel) => {
      if (channelWithCompanyID(channel)) {
        dispatch(setChannelIdToCompanyId(channel.cid, channel.data.companyID));
        dispatch(
          setTotalUnreadMessagesByCompany(
            channel.data.companyID,
            channel.countUnread(),
          ),
        );
      }
    });
  };

  const clientEventHandler = (event: Event<ExtendableGenerics>) => {
    if (event.unread_count !== undefined) {
      dispatch(setTotalUnreadMessages(event.unread_count));

      if (!isClient) return;
      // the event object does not have the channel data with the company id
      // it only has the channel id known as cid getstream field.
      // However a cid does not work to store the unread count. We need the company id.
      // For that we do have a mapping of cid to company id which will help us to
      // lookup the company id from the cid.
      const companyId = event?.cid
        ? channelIdToCompanyId?.[event?.cid]
        : undefined;

      // once we get the company id from the cid, we do update the unread count state.
      if (companyId) {
        dispatch(
          setTotalUnreadMessagesByCompany(companyId, event.unread_count ?? 0),
        );
      }
    }
  };

  /**
   * This effect is responsible of fetching the unread count for each company
   */
  React.useEffect(() => {
    if (!chatClient) return;
    if (isClient && EnableManyCompanies) {
      fetchUnreadMessagesCountByCompany(chatClient);
    }
  }, [isClient, EnableManyCompanies, chatClient]);

  /**
   * This effect is responsible of loading unread chat notifications and subscribing to new events.
   */
  useEffect(() => {
    if (
      !chatClient ||
      !chatClient.user ||
      !currentUserChatId ||
      disabledMessagesNotifications ||
      disabledProductNotification
    ) {
      return;
    }

    // Listen for new events. This will also update the unread count for each company.
    chatClient.on(clientEventHandler);

    // initializing the unread message redux state on sidebar mount from client user data
    if (!totalUnreadMessages && totalUnreadMessages !== 0) {
      if (typeof chatClient.user.unread_count === 'number') {
        dispatch(setTotalUnreadMessages(chatClient.user.unread_count));
      }
    }

    return () => {
      chatClient.off(clientEventHandler);
    };
  }, [
    chatClient,
    currentUserChatId,
    channelIdToCompanyId,
    isClient,
    disabledMessagesNotifications,
    disabledProductNotification,
    EnableManyCompanies,
  ]);

  // Update the document title based on the user type and notification counts
  useEffect(() => {
    const notificationCount = isClient
      ? totalClientNotifications
      : totalInternalNotifications;
    const pageTitle = isClient
      ? portalConfig.metadata?.title || portalConfig.name
      : portalConfig.metadata.titleOverride;

    document.title = `${
      notificationCount > 0
        ? `(${formatNotificationCount(notificationCount)})`
        : ''
    } ${pageTitle}`;
  }, [totalClientNotifications, totalInternalNotifications, portalConfig]);
};
